import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InventoryQueryKeys } from './queryKeys';
import { InventoryService, SavedQueryUpsertAppModel } from '@api';

export function useCreateSavedInventoryQuery() {
  const queryClient = useQueryClient();
  const { mutate: createSavedInventoryQuery, ...rest } = useMutation({
    mutationFn: async ({ name, query }: { name: string; query: string }) =>
      InventoryService.createSavedQuery({
        savedQueryUpsertAppModel: { name, query, personal: false },
      }),
    onSettled: () => queryClient.invalidateQueries([InventoryQueryKeys.InventoryQuery]),
  });

  return {
    createSavedInventoryQuery,
    inventoryQueryId: rest.data?.id,
    ...rest,
  };
}

export function useListSavedQueries() {
  const fetchMethod = async () => {
    const { data } = await InventoryService.listSavedQueries();

    return data;
  };

  const { data: savedQueries, ...rest } = useQuery([InventoryQueryKeys.InventoryQuery], fetchMethod, {
    initialData: [],
  });

  return {
    savedQueries,
    ...rest,
  };
}

export function useGetSavedQuery(id: string) {
  const {
    data: savedQuery,
    isFetched: isSavedQueryFetched,
    ...rest
  } = useQuery({
    queryKey: [InventoryQueryKeys.InventoryQuery, id],
    queryFn: async () => InventoryService.getSavedQuery({ id }),
  });

  return {
    savedQuery,
    isSavedQueryFetched,
    ...rest,
  };
}

export function useCanDeleteSavedQuery(id: string) {
  const { data: canDeleteSavedQuery, isLoading: isCanDeleteSavedQueryLoading } = useQuery({
    queryKey: [InventoryQueryKeys.CanDeleteInventoryQuery, id],
    queryFn: async () => InventoryService.appCanDeleteSavedQuery({ id }),
  });

  return {
    canDeleteSavedQuery,
    isCanDeleteSavedQueryLoading,
  };
}

export function useUpdateSavedQuery(id: string) {
  const queryClient = useQueryClient();

  const {
    mutate: updateSavedQuery,
    isLoading: isUpdateSavedQueryLoading,
    data: updatedSavedQuery,
  } = useMutation({
    mutationFn: async (payload: SavedQueryUpsertAppModel) =>
      InventoryService.updateSavedQuery({
        id,
        savedQueryUpsertAppModel: payload,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [InventoryQueryKeys.InventoryQuery, id] });
      queryClient.refetchQueries({ queryKey: [InventoryQueryKeys.InventoryQuery, id] });
      queryClient.refetchQueries({ queryKey: [InventoryQueryKeys.InventoryQuery] });
    },
  });

  return {
    updateSavedQuery,
    isUpdateSavedQueryLoading,
    updatedSavedQuery,
  };
}

export function useDeleteSavedQuery(id: string) {
  const queryClient = useQueryClient();

  const {
    mutate: deleteSavedQuery,
    isLoading: isDeleteSavedQueryLoading,
    data: deletedSavedQuery,
  } = useMutation({
    mutationFn: async () => InventoryService.deleteSavedQuery({ id }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [InventoryQueryKeys.InventoryQuery] }),
  });

  return {
    deleteSavedQuery,
    isDeleteSavedQueryLoading,
    deletedSavedQuery,
  };
}
